import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useAuth } from '@/contexts/AuthContext';
import { CheckCircle2, XCircle, Download, Search, Loader2, FileText, Car, User, Phone } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function PdfDebugPage() {
  const { user } = useAuth();
  const [searchQuery, setSearchQuery] = useState('');
  const [inspections, setInspections] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selectedInspection, setSelectedInspection] = useState(null);
  const [debugResult, setDebugResult] = useState(null);
  const [diagnosing, setDiagnosing] = useState(false);
  const [downloading, setDownloading] = useState({});

  const getToken = () => localStorage.getItem('token') || sessionStorage.getItem('token');

  const searchInspections = async () => {
    if (!searchQuery.trim()) return;
    setSearching(true);
    setInspections([]);
    setSelectedInspection(null);
    setDebugResult(null);
    try {
      const token = getToken();
      const res = await axios.get(`${API_URL}/api/inspections`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { search: searchQuery.trim(), limit: 20, page: 1 },
      });
      const data = res.data?.data || res.data?.inspections || [];
      setInspections(data);
    } catch (err) {
      console.error('Search failed:', err);
    } finally {
      setSearching(false);
    }
  };

  const runDiagnostic = async (inspId) => {
    setDiagnosing(true);
    setDebugResult(null);
    try {
      const token = getToken();
      const res = await axios.get(`${API_URL}/api/inspections/${inspId}/pdf-debug?brand=wisedrive`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setDebugResult(res.data);
    } catch (err) {
      setDebugResult({ error: err.response?.data?.detail || err.message, steps: [] });
    } finally {
      setDiagnosing(false);
    }
  };

  const handleDownload = (inspId, brand = 'wisedrive') => {
    setDownloading(prev => ({ ...prev, [`${inspId}-${brand}`]: true }));
    const token = getToken();
    const pdfUrl = `${API_URL}/api/inspections/${inspId}/report-pdf?brand=${brand}&token=${encodeURIComponent(token)}`;
    window.open(pdfUrl, '_blank');
    setTimeout(() => setDownloading(prev => ({ ...prev, [`${inspId}-${brand}`]: false })), 3000);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') searchInspections();
  };

  if (!user) return <div className="p-8 text-center">Please log in first.</div>;

  return (
    <div className="min-h-screen bg-gray-50 p-6" data-testid="pdf-debug-page">
      <div className="max-w-4xl mx-auto">
        <h1 className="text-2xl font-bold mb-1">PDF Download & Diagnostics</h1>
        <p className="text-gray-500 mb-6 text-sm">
          Search by car number, customer name, or phone number. Then download or diagnose PDFs.
        </p>

        {/* Search Bar */}
        <div className="bg-white rounded-xl border p-5 mb-5">
          <div className="flex gap-3">
            <div className="flex-1 relative">
              <Input
                data-testid="pdf-search-input"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Search by car number, customer name, or phone..."
                className="pl-10"
              />
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            </div>
            <Button
              data-testid="pdf-search-btn"
              onClick={searchInspections}
              disabled={searching || !searchQuery.trim()}
            >
              {searching ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Search
            </Button>
          </div>
        </div>

        {/* Results */}
        {inspections.length > 0 && (
          <div className="bg-white rounded-xl border mb-5" data-testid="pdf-search-results">
            <div className="px-5 py-3 border-b bg-gray-50 rounded-t-xl">
              <span className="text-sm font-medium text-gray-700">
                {inspections.length} inspection{inspections.length !== 1 ? 's' : ''} found
              </span>
            </div>
            <div className="divide-y max-h-[400px] overflow-y-auto">
              {inspections.map((insp) => (
                <div
                  key={insp.id}
                  className={`px-5 py-3 flex items-center justify-between hover:bg-blue-50 transition-colors cursor-pointer ${
                    selectedInspection?.id === insp.id ? 'bg-blue-50 border-l-4 border-blue-500' : ''
                  }`}
                  onClick={() => { setSelectedInspection(insp); setDebugResult(null); }}
                  data-testid={`pdf-inspection-row-${insp.id}`}
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3">
                      <span className="font-semibold text-sm">{insp.car_number || 'No plate'}</span>
                      <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">
                        {insp.car_make} {insp.car_model}
                      </span>
                      {insp.status && (
                        <span className={`text-xs px-2 py-0.5 rounded-full ${
                          insp.status === 'Completed' ? 'bg-green-100 text-green-700' :
                          insp.status === 'In Progress' ? 'bg-yellow-100 text-yellow-700' :
                          'bg-gray-100 text-gray-600'
                        }`}>
                          {insp.status}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-4 mt-1 text-xs text-gray-500">
                      {insp.customer_name && (
                        <span className="flex items-center gap-1">
                          <User className="h-3 w-3" /> {insp.customer_name}
                        </span>
                      )}
                      {insp.customer_phone && (
                        <span className="flex items-center gap-1">
                          <Phone className="h-3 w-3" /> {insp.customer_phone}
                        </span>
                      )}
                      {insp.city && <span>{insp.city}</span>}
                      {insp.scheduled_date && <span>{insp.scheduled_date}</span>}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 ml-4 shrink-0">
                    <Button
                      size="sm"
                      variant="outline"
                      data-testid={`pdf-download-wisedrive-${insp.id}`}
                      disabled={downloading[`${insp.id}-wisedrive`]}
                      onClick={(e) => { e.stopPropagation(); handleDownload(insp.id, 'wisedrive'); }}
                    >
                      {downloading[`${insp.id}-wisedrive`]
                        ? <Loader2 className="h-3 w-3 animate-spin mr-1" />
                        : <Download className="h-3 w-3 mr-1" />
                      }
                      WiseDrive PDF
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      data-testid={`pdf-download-sheerdrive-${insp.id}`}
                      disabled={downloading[`${insp.id}-sheerdrive`]}
                      onClick={(e) => { e.stopPropagation(); handleDownload(insp.id, 'sheerdrive'); }}
                    >
                      {downloading[`${insp.id}-sheerdrive`]
                        ? <Loader2 className="h-3 w-3 animate-spin mr-1" />
                        : <Download className="h-3 w-3 mr-1" />
                      }
                      Sheerdrive PDF
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {searching === false && searchQuery && inspections.length === 0 && (
          <div className="bg-white rounded-xl border p-8 text-center text-gray-500 mb-5">
            No inspections found for "{searchQuery}"
          </div>
        )}

        {/* Diagnostic Panel */}
        {selectedInspection && (
          <div className="bg-white rounded-xl border p-5" data-testid="pdf-diagnostic-panel">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-lg font-semibold">
                  Diagnostic: {selectedInspection.car_number || selectedInspection.id}
                </h2>
                <p className="text-xs text-gray-500">ID: {selectedInspection.id}</p>
              </div>
              <Button
                data-testid="pdf-run-diagnostic-btn"
                onClick={() => runDiagnostic(selectedInspection.id)}
                disabled={diagnosing}
                size="sm"
              >
                {diagnosing ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Search className="h-4 w-4 mr-2" />}
                Run Diagnostic
              </Button>
            </div>

            {debugResult && (
              <div data-testid="pdf-debug-results">
                <div className={`flex items-center gap-2 mb-3 text-sm font-medium ${
                  debugResult.overall === 'ok' ? 'text-green-700' : 'text-red-700'
                }`}>
                  {debugResult.overall === 'ok'
                    ? <><CheckCircle2 className="h-5 w-5" /> All Checks Passed</>
                    : <><XCircle className="h-5 w-5" /> {debugResult.error || 'Issues Found'}</>
                  }
                </div>
                <div className="space-y-2">
                  {debugResult.steps?.map((step, i) => (
                    <div key={i} className={`flex items-start gap-2 p-2.5 rounded-lg text-sm ${
                      step.status === 'ok' ? 'bg-green-50' : 'bg-red-50'
                    }`}>
                      {step.status === 'ok'
                        ? <CheckCircle2 className="h-4 w-4 text-green-600 mt-0.5 shrink-0" />
                        : <XCircle className="h-4 w-4 text-red-600 mt-0.5 shrink-0" />
                      }
                      <div>
                        <span className="font-medium">
                          {step.step.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
                        </span>
                        {Object.entries(step)
                          .filter(([k]) => !['step', 'status'].includes(k))
                          .map(([k, v]) => (
                            <span key={k} className="ml-3 text-xs text-gray-600">
                              {k}: <strong>{String(v)}</strong>
                            </span>
                          ))}
                      </div>
                    </div>
                  ))}
                </div>
                {debugResult.overall === 'ok' && (
                  <div className="mt-4 pt-3 border-t flex gap-2">
                    <Button
                      size="sm"
                      data-testid="pdf-diag-download-wisedrive"
                      onClick={() => handleDownload(selectedInspection.id, 'wisedrive')}
                      className="bg-blue-600 hover:bg-blue-700"
                    >
                      <Download className="h-4 w-4 mr-1" /> Download WiseDrive PDF
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      data-testid="pdf-diag-download-sheerdrive"
                      onClick={() => handleDownload(selectedInspection.id, 'sheerdrive')}
                    >
                      <Download className="h-4 w-4 mr-1" /> Download Sheerdrive PDF
                    </Button>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* URL Info */}
        <div className="mt-6 text-xs text-gray-400 text-center">
          This page is available at <code className="bg-gray-100 px-1.5 py-0.5 rounded">/admin/pdf-debug</code>
        </div>
      </div>
    </div>
  );
}
